import { useState } from 'react';
import { motion } from 'framer-motion';
import type { GameState, PlayerAction } from '@/types';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

interface ActionPanelProps {
  state: GameState;
  validActions: string[];
  onAction: (action: PlayerAction) => void;
}

export default function ActionPanel({ state, validActions, onAction }: ActionPanelProps) {
  const [raiseAmount, setRaiseAmount] = useState(state.minRaise);
  const currentPlayer = state.players[state.currentPlayerIndex];
  
  if (!currentPlayer?.isHuman) return null;



  const toCall = state.currentBet - currentPlayer.currentBet;
  const maxRaise = currentPlayer.chips + currentPlayer.currentBet;
  const potSize = state.pots.reduce((sum, p) => sum + p.amount, 0);

  const handleRaiseChange = (value: number[]) => {
    setRaiseAmount(value[0]);
  };

  const quickBets = [
    { label: '1/2 底池', amount: Math.floor(potSize * 0.5) + state.currentBet },
    { label: '底池', amount: potSize + state.currentBet },
    { label: '2x 底池', amount: Math.floor(potSize * 2) + state.currentBet },
    { label: '全押', amount: maxRaise },
  ];

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-6 left-2/3 -translate-x-1/2 z-50"
    >
      <div className="bg-slate-900/95 backdrop-blur-lg rounded-2xl p-4 border border-slate-700 shadow-2xl">
        {/* 需要跟注的提示 */}
        {toCall > 0 && (
          <div className="text-center mb-3">
            <span className="text-amber-400 font-bold">
              需要跟注: {toCall.toLocaleString()}
            </span>
          </div>
        )}

        {/* 主要操作按钮 */}
        <div className="flex items-center gap-3">
          {/* 弃牌 */}
          {validActions.includes('fold') && (
            <Button
              onClick={() => onAction({ type: 'fold' })}
              variant="destructive"
              className="px-6 py-6 text-lg font-bold bg-red-600 hover:bg-red-500"
            >
              弃牌
            </Button>
          )}

          {/* 过牌 */}
          {validActions.includes('check') && (
            <Button
              onClick={() => onAction({ type: 'check' })}
              variant="secondary"
              className="px-6 py-6 text-lg font-bold bg-slate-600 hover:bg-slate-500"
            >
              过牌
            </Button>
          )}

          {/* 跟注 */}
          {validActions.includes('call') && (
            <Button
              onClick={() => onAction({ type: 'call' })}
              variant="default"
              className="px-6 py-6 text-lg font-bold bg-blue-600 hover:bg-blue-500"
            >
              跟注 {toCall.toLocaleString()}
            </Button>
          )}

          {/* 加注滑块和按钮 */}
          {validActions.includes('raise') && (
            <div className="flex flex-col gap-2 min-w-[200px]">
              <div className="flex items-center gap-2">
                <Slider
                  value={[raiseAmount]}
                  onValueChange={handleRaiseChange}
                  min={state.minRaise}
                  max={maxRaise}
                  step={10}
                  className="flex-1"
                />
                <span className="text-white font-mono w-20 text-right">
                  {raiseAmount.toLocaleString()}
                </span>
              </div>
              <div className="flex gap-1">
                {quickBets.map((bet) => (
                  <button
                    key={bet.label}
                    onClick={() => setRaiseAmount(Math.min(bet.amount, maxRaise))}
                    className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-white/80 transition-colors"
                  >
                    {bet.label}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => onAction({ type: 'raise', amount: raiseAmount })}
                className="w-full py-4 text-lg font-bold bg-amber-600 hover:bg-amber-500"
              >
                加注
              </Button>
            </div>
          )}

          {/* 全押 */}
          {validActions.includes('all-in') && (
            <Button
              onClick={() => onAction({ type: 'all-in' })}
              className="px-6 py-6 text-lg font-bold bg-purple-600 hover:bg-purple-500"
            >
              全押
            </Button>
          )}
        </div>

        {/* 当前玩家信息 */}
        <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between text-sm text-slate-400">
          <span>筹码: {currentPlayer.chips.toLocaleString()}</span>
          <span>已下注: {currentPlayer.totalBetThisRound.toLocaleString()}</span>
        </div>
      </div>
    </motion.div>
  );
}
